import React, { useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import axios from 'axios';
import moment from 'moment';

// ====================================================================
// ESDA Analysis Tool — 4-step wizard (new design, hotfix/esda_new_tool)
// Select problems → upload required files → run analysis → review results.
// No date picker (removed per new design). Wired to the existing
// /api/dataguard/* backend; Brand + extra columns are stubbed "—" until
// the backend emits them (frontend-shell-first build).
// ====================================================================

const ESDA_API = '';
const qid = (id) => 'PS-' + String(id).padStart(2, '0');
const today = () => moment().format('YYYY-MM-DD');

// ---- Result column builders (per-problem spec: Table for all problems_ESDA) ----
// Base columns map to what the backend stores today; Brand / Product Batch /
// the extras are stubbed "—" until the backend emits them.
// Normalize empty/NaN-ish values to an em-dash so the table never shows
// literal "nan"/"None" (e.g. PS-07 special stock is blank for flagged rows).
const clean = (v) => {
  const s = v == null ? '' : String(v).trim();
  return (s === '' || ['nan', 'none', 'nat', 'null'].includes(s.toLowerCase())) ? '—' : s;
};
const COL = {
  cmo:    { key: 'cmo',    label: 'CMO',                   get: (r) => clean(r.vendor_name) },
  brand:  { key: 'brand',  label: 'Brand',                 get: (r) => clean(r.brand) },
  po:     { key: 'po',     label: 'PO Number',             get: (r) => clean(r.po_number) },
  pbatch: { key: 'pbatch', label: 'Product Batch Number',  get: (r) => clean(r.product_batch_number) },
  pcode:  { key: 'pcode',  label: 'Product Code',          get: (r) => clean(r.product_code || r.material_code) },
  mbatch: { key: 'mbatch', label: 'Material Batch Number', get: (r) => clean(r.batch_number) },
  mcode:  { key: 'mcode',  label: 'Material Code',         get: (r) => clean(r.bom_material_code || r.material_code) },
};
const x = (label, field) => ({ key: field, label, get: (r) => clean(r[field]) });

// ---- Problem catalog (17) — metadata + per-problem display columns ----
const PROBLEMS = [
  { id: 1,  title: 'Each Material Batch Should Have A Unique Material Code', files: ['SAP Inventory', 'SAP RESB', 'SAP ME2N'], owner: 'BMS User', category: 'inventory', severity: 'high',
    action: 'BMS user to ensure the identified batch has only one material association.',
    cols: [COL.cmo, COL.brand, COL.po, COL.mbatch, COL.mcode] },
  { id: 2,  title: 'Date Of Manufacturing Missing In Inventory', files: ['SAP Inventory'], owner: 'BMS User', category: 'inventory', severity: 'medium',
    action: 'BMS users to update Date Of Manufacturing for these batches in SAP.',
    cols: [COL.cmo, COL.brand, COL.mbatch] },
  { id: 3,  title: 'Date Of Expiry Missing In Inventory', files: ['SAP Inventory'], owner: 'BMS User', category: 'inventory', severity: 'high',
    action: 'BMS users to update Date Of Expiry for these batches in SAP.',
    cols: [COL.cmo, COL.brand, COL.mbatch] },
  { id: 4,  title: 'Consumed Batch Is Expired', files: ['SAP Inventory'], owner: 'BMS User', category: 'inventory', severity: 'critical',
    action: 'BMS users to update Date Of Expiry or replace allocation for these expired batches in SAP.',
    cols: [COL.cmo, COL.brand, COL.po, COL.mbatch, x('Expiry Date', 'expiry_date')] },
  { id: 5,  title: 'Batch Not Available In Inventory (Special Stock With Vendor)', files: ['SAP Inventory'], owner: 'BMS User', category: 'inventory', severity: 'high',
    action: "BMS user to change storage location to 'Special Stocks At Vendor'; Mosymphony to sync, then perform allocation.",
    cols: [COL.cmo, COL.brand, COL.po, COL.mbatch, x('Storage Location', 'location')] },
  { id: 6,  title: 'Planned Allocated Batch Now Available In Special Stock With Vendor', files: ['SAP Inventory'], owner: 'BMS User', category: 'inventory', severity: 'medium',
    action: 'BMS user to perform allocation in Mosymphony for these batches.',
    cols: [COL.cmo, COL.brand, COL.po, COL.mbatch, x('Special Stocks At Vendor', 'stock_type')] },
  { id: 7,  title: 'Batch Not Available For Allocation (Special Stock With Vendor)', files: ['SAP Inventory'], owner: 'BMS User', category: 'inventory', severity: 'high',
    action: 'BMS user to perform allocation for these POs using the identified batches in special stock at vendor.',
    cols: [COL.cmo, COL.brand, COL.po, COL.mbatch, COL.mcode, x('Storage Location', 'location'), x('Special Stocks At Vendor', 'stock_type')] },
  { id: 8,  title: 'Stock Insufficient During Process Flow', files: ['SAP Inventory'], owner: 'BMS User', category: 'inventory', severity: 'critical',
    action: 'BMS user to transfer stock for the batches that do not have sufficient stock.',
    cols: [COL.cmo, COL.brand, COL.po, COL.mbatch, x('Type', 'type'), x('Required Qty', 'required_qty'), x('Available Qty', 'available_qty')] },
  { id: 9,  title: 'PO For New SKU, Not Yet Added In Mosymphony', files: ['SAP ME2N'], owner: 'Operations + BMS', category: 'master-data', severity: 'medium',
    action: 'BMS to share new SKU details. Operations to update Mosymphony master data with new SKUs.',
    cols: [COL.cmo, COL.brand, COL.po, COL.pcode] },
  { id: 10, title: 'PO For Existing SKU, BoM Components Missing In Mosymphony', files: ['SAP ME2N', 'SAP RESB'], owner: 'Operations + BMS', category: 'master-data', severity: 'medium',
    action: 'BMS to share updated SKU details including missing components. Operations to update Mosymphony master data.',
    cols: [COL.cmo, COL.brand, COL.po, COL.pcode, COL.mcode] },
  { id: 11, title: 'Storage Location Is Missing', files: ['SAP ME2N'], owner: 'BMS User', category: 'po-data', severity: 'high',
    action: 'BMS user to update the storage location for these POs in SAP.',
    cols: [COL.cmo, COL.brand, COL.po] },
  { id: 12, title: 'Confirmation Control Key Is Missing', files: ['SAP ME2N'], owner: 'BMS User', category: 'po-data', severity: 'medium',
    action: 'BMS user to update the confirmation control key for these POs in SAP.',
    cols: [COL.cmo, COL.brand, COL.po] },
  { id: 13, title: 'Storage Location Is Wrong', files: ['SAP ME2N'], owner: 'BMS User', category: 'po-data', severity: 'low',
    action: 'BMS user to update the storage location for these POs in SAP.',
    cols: [COL.cmo, COL.brand, COL.po, x('Storage Location', 'mosy_storage_location'), x('Expected Storage Location', 'sap_storage_location')] },
  { id: 14, title: 'Confirmation Control Key Is Wrong', files: ['SAP ME2N'], owner: 'BMS User', category: 'po-data', severity: 'medium',
    action: 'BMS user to update the confirmation control key for these POs in SAP.',
    cols: [COL.cmo, COL.brand, COL.po, x('Confirmation Control Key', 'mosy_conf_ctrl'), x('Expected Confirmation Control Key', 'sap_conf_ctrl')] },
  { id: 15, title: 'Wrong Batch Document Format Received', files: [], owner: 'BMS User', category: 'documents', severity: 'low',
    action: 'BMS user to request resubmission of the document from the CMO, in the correct format.',
    cols: [COL.cmo, COL.brand, COL.po, COL.pbatch, x('Expected Format', 'expected_format'), x('Received Format', 'received_format')] },
  { id: 16, title: 'Batches Not Allocated, Allocation Owned By BMS', files: ['SAP ME2N'], owner: 'BMS User', category: 'po-data', severity: 'high',
    action: 'BMS user to perform allocation for these POs.',
    cols: [COL.cmo, COL.brand, COL.po, COL.mcode] },
  { id: 17, title: 'SKU Present In SAP, Missing In Mosymphony', files: ['SAP AMPL'], owner: 'Operations + BMS', category: 'master-data', severity: 'medium',
    action: 'BMS to confirm if new SKUs need to be added; Operations to update Mosymphony master data.',
    cols: [COL.cmo, COL.brand, COL.pcode] },
];

const FILE_META = {
  'SAP Inventory': { desc: 'MB52 / Inventory Snapshot' },
  'SAP RESB':      { desc: 'Reservation / BoM Table' },
  'SAP ME2N':      { desc: 'Purchase Order List' },
  'SAP AMPL':      { desc: 'Approved Manufacturer Parts List' },
};

const CATEGORY_LABELS = {
  inventory: 'Inventory & Batches',
  'po-data': 'PO Data Quality',
  'master-data': 'Master Data Sync',
  documents: 'Documents',
};

const SEVERITY = {
  critical: { label: 'Critical', color: '#FF0004', soft: '#FDE6E7' },
  high:     { label: 'High',     color: '#EC6535', soft: '#FCE8DD' },
  medium:   { label: 'Medium',   color: '#286ACF', soft: '#E8F0FB' },
  low:      { label: 'Low',      color: '#707070', soft: '#F1F1F3' },
};

const STEPS = ['Select Problems', 'Upload Data', 'Run Analysis', 'Results'];

// ---- CSV helpers ----
const csvEscape = (s) => {
  const v = s == null ? '' : String(s);
  return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
};
const downloadCSV = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const styles = () => ({
  root: { padding: '1.5rem', backgroundColor: '#F9F9FC', minHeight: '100vh', fontFamily: 'Roboto, sans-serif', color: '#000' },
  // stepper
  stepper: { display: 'flex', alignItems: 'center', maxWidth: '760px', margin: '0 auto 1.5rem' },
  stepItem: { display: 'flex', alignItems: 'center', flex: 1 },
  stepDot: { width: '28px', height: '28px', borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500, fontSize: '13px', flexShrink: 0 },
  stepDotIdle: { background: '#fff', color: '#707070', border: '1px solid #B4B4B4' },
  stepDotActive: { background: '#EC6535', color: '#fff' },
  stepDotDone: { background: '#329429', color: '#fff' },
  stepLabel: { fontSize: '12px', marginLeft: '8px', whiteSpace: 'nowrap' },
  stepLine: { height: '2px', flex: 1, background: '#E4E2E3', margin: '0 8px' },
  stepLineActive: { background: '#EC6535' },
  // card
  card: { background: '#fff', border: '1px solid #E4E2E3', borderRadius: '5px' },
  cardPad: { padding: '24px' },
  h1: { fontSize: '20px', fontWeight: 500, color: '#000' },
  muted: { color: '#707070', fontSize: '13px' },
  // problem grid
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '1rem' },
  problemCard: { border: '1px solid #E4E2E3', background: '#fff', borderRadius: '5px', padding: '16px', cursor: 'pointer', transition: 'all .15s', display: 'flex', gap: '12px', alignItems: 'flex-start' },
  problemCardSel: { borderColor: '#EC6535', boxShadow: '0 0 0 1px #EC6535', background: '#FFFCFA' },
  checkbox: { width: '18px', height: '18px', borderRadius: '4px', border: '1.5px solid #B4B4B4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '12px', background: '#fff', marginTop: '2px' },
  checkboxOn: { background: '#EC6535', borderColor: '#EC6535', color: '#fff' },
  pcTitle: { fontSize: '14px', fontWeight: 500, lineHeight: 1.3 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' },
  chip: { display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '30px', fontSize: '11px', fontWeight: 500 },
  chipGrey: { background: '#F1F1F3', color: '#707070' },
  // sticky footer bar
  footerBar: { position: 'sticky', bottom: '12px', zIndex: 10, marginTop: '1.5rem', background: '#fff', border: '1px solid #E4E2E3', borderRadius: '5px', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  // buttons
  btnOrange: { background: '#EC6535', color: '#fff', padding: '9px 22px', borderRadius: '5px', fontSize: '14px', fontWeight: 500, border: 'none', cursor: 'pointer' },
  btnOrangeOff: { background: '#F4B7A0', cursor: 'not-allowed' },
  btnWhite: { background: '#fff', color: '#000', padding: '9px 22px', borderRadius: '5px', fontSize: '14px', fontWeight: 500, border: '1px solid #B4B4B4', cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#707070', border: '1px solid #B4B4B4', borderRadius: '5px', padding: '6px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' },
  btnGhostRed: { color: '#FF0004', borderColor: '#F8C5C7' },
  btnLink: { color: '#286ACF', fontWeight: 500, fontSize: '13px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' },
  // upload
  uploadGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '1rem' },
  uploadZone: { border: '1.5px dashed #B4B4B4', borderRadius: '8px', padding: '24px', textAlign: 'center', background: '#fff' },
  uploadZoneOk: { borderStyle: 'solid', borderColor: '#329429', background: '#E6F4E5' },
  // progress
  progressBar: { height: '8px', borderRadius: '30px', background: '#F1F1F3', overflow: 'hidden', marginTop: '8px', position: 'relative' },
  progressFill: { height: '100%', background: 'linear-gradient(135deg,#073270,#3B173F)', transition: 'width .3s' },
  '@keyframes esdaIndet': { '0%': { transform: 'translateX(-100%)' }, '100%': { transform: 'translateX(350%)' } },
  progressIndet: { position: 'absolute', top: 0, height: '100%', width: '30%', borderRadius: '30px', background: 'linear-gradient(135deg,#073270,#3B173F)', animation: '$esdaIndet 1.1s infinite ease-in-out' },
  statusRow: { display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', border: '1px solid #E4E2E3', borderRadius: '5px', padding: '12px 16px', marginTop: '8px' },
  sevBar: { width: '4px', borderRadius: '4px', alignSelf: 'stretch', minHeight: '32px' },
  // results
  filterBar: { display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' },
  searchField: { flex: '1 1 360px', minWidth: '300px' },
  searchPill: { display: 'flex', alignItems: 'center', border: '1px solid #B4B4B4', borderRadius: '30px', overflow: 'hidden', background: '#fff', paddingRight: '3px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', padding: '10px 16px', fontSize: '13px', fontFamily: 'Roboto, sans-serif', background: 'transparent' },
  searchBtn: { background: '#EC6535', color: '#fff', border: 'none', borderRadius: '30px', padding: '8px 26px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' },
  filterField: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '13px', fontWeight: 500, color: '#000' },
  select: { border: '1px solid #B4B4B4', borderRadius: '5px', padding: '9px 10px', fontSize: '13px', background: '#fff', minWidth: '150px', fontFamily: 'Roboto, sans-serif' },
  btnBlue: { background: '#286ACF', color: '#fff', border: 'none', borderRadius: '5px', padding: '9px 18px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
  resultCard: { border: '1px solid #E4E2E3', borderRadius: '5px', background: '#fff', marginTop: '16px', overflow: 'hidden' },
  resultHead: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px', cursor: 'pointer' },
  countNum: { fontSize: '24px', fontWeight: 500, color: '#000', textAlign: 'right' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '8px 12px', background: '#E4E2E3', color: '#000', fontWeight: 500, whiteSpace: 'nowrap' },
  td: { padding: '8px 12px', borderBottom: '1px solid #F1F1F3', color: '#000' },
  actionBox: { margin: '16px', padding: '12px 14px', background: '#E8F0FB', borderRadius: '5px' },
});

const Esda = ({ classes }) => {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [uploaded, setUploaded] = useState({});            // { fileType: {name, size} }
  const [progress, setProgress] = useState(null);          // run-progress response
  const [running, setRunning] = useState(false);
  const [analysisDone, setAnalysisDone] = useState(false);
  const [results, setResults] = useState([]);              // flat items from /results
  const [loadingResults, setLoadingResults] = useState(false);
  const [search, setSearch] = useState('');
  const [fCmo, setFCmo] = useState('');
  const [fBrand, setFBrand] = useState('');
  const [fProblem, setFProblem] = useState('');
  const [err, setErr] = useState('');
  const pollRef = useRef(null);
  const preRunIdRef = useRef(0);
  const fileInputRef = useRef(null);
  const pendingFileType = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ---------- Step 1: selection ----------
  const toggleProblem = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = () => setSelected(new Set(PROBLEMS.map((p) => p.id)));
  const clearAll = () => setSelected(new Set());

  // ---------- Step 2: required files ----------
  const requiredFiles = useMemo(() => {
    const s = new Set();
    selected.forEach((id) => {
      const p = PROBLEMS.find((x) => x.id === id);
      (p.files || []).forEach((f) => s.add(f));
    });
    return [...s];
  }, [selected]);

  const allUploaded = requiredFiles.length > 0 && requiredFiles.every((f) => uploaded[f]);

  const pickFile = (fileType) => {
    pendingFileType.current = fileType;
    if (fileInputRef.current) fileInputRef.current.click();
  };
  const onFileChosen = async (e) => {
    const file = e.target.files[0];
    const fileType = pendingFileType.current;
    e.target.value = '';
    if (!file || !fileType) return;
    setErr('');
    const form = new FormData();
    form.append('file', file);
    form.append('file_type', fileType);
    try {
      const res = await axios.post(`${ESDA_API}/api/dataguard/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploaded((prev) => ({ ...prev, [fileType]: { name: res.data.filename || file.name, size: (file.size / 1048576).toFixed(1) + ' MB' } }));
    } catch (e2) {
      setErr(`Failed to upload ${fileType}: ${e2.response?.data?.error || e2.message}`);
    }
  };

  // ---------- Step 3: run + poll ----------
  const startPolling = (preRunId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      axios.get(`${ESDA_API}/api/dataguard/run-progress`).then((res) => {
        const data = res.data;
        if (data.run_id && data.run_id > preRunId) {
          setProgress(data);
          if (data.run_status && !['running', 'none'].includes(data.run_status)) {
            clearInterval(pollRef.current);
            setRunning(false);
            setAnalysisDone(true);
          }
        }
      }).catch(() => {});
    }, 3000);
  };

  const runAnalysis = async () => {
    setErr(''); setRunning(true); setAnalysisDone(false); setProgress({ run_id: null, run_status: 'running', steps: [] });
    setStep(3);
    try {
      const ids = [...selected].map(qid);
      const res = await axios.post(`${ESDA_API}/api/dataguard/run`, { problem_ids: ids });
      preRunIdRef.current = res.data.pre_run_id || 0;
      startPolling(preRunIdRef.current);
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Failed to start analysis.'); setRunning(false);
    }
  };
  const stopAnalysis = async () => { if (pollRef.current) clearInterval(pollRef.current); setRunning(false); try { await axios.post(`${ESDA_API}/api/dataguard/stop`); } catch (e) {} };
  const resumeAnalysis = async () => { setRunning(true); try { await axios.post(`${ESDA_API}/api/dataguard/resume`); } catch (e) {} startPolling(preRunIdRef.current); };
  const resetAnalysis = async () => { setErr(''); setRunning(true); setAnalysisDone(false); setProgress({ run_id: null, run_status: 'running', steps: [] }); try { const res = await axios.post(`${ESDA_API}/api/dataguard/reset`); preRunIdRef.current = res.data.pre_run_id || 0; startPolling(preRunIdRef.current); } catch (e2) { setErr('Failed to reset analysis.'); setRunning(false); } };

  // ---------- Step 4: results ----------
  const fetchResults = () => {
    setLoadingResults(true);
    axios.get(`${ESDA_API}/api/dataguard/results/${today()}`)
      .then((res) => { setResults(res.data.results || []); setLoadingResults(false); })
      .catch(() => { setResults([]); setLoadingResults(false); });
  };
  const goToResults = () => { setStep(4); fetchResults(); };

  // group flat items by query_id → problem
  const resultsByProblem = useMemo(() => {
    const map = {};
    results.forEach((r) => {
      const k = r.query_id;
      if (!map[k]) map[k] = [];
      map[k].push(r);
    });
    return map;
  }, [results]);

  const cmoOptions = useMemo(() => [...new Set(results.map((r) => r.vendor_name).filter(Boolean))].sort(), [results]);
  const brandOptions = useMemo(() => [...new Set(results.map((r) => r.brand).filter(Boolean))].sort(), [results]);

  const rowMatchesFilters = (r) => {
    if (fCmo && r.vendor_name !== fCmo) return false;
    if (fBrand && (r.brand || '') !== fBrand) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [r.po_number, r.batch_number, r.material_code, r.vendor_name, r.issue_detail].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const restart = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setStep(1); setSelected(new Set()); setUploaded({}); setProgress(null);
    setRunning(false); setAnalysisDone(false); setResults([]);
    setSearch(''); setFCmo(''); setFBrand(''); setFProblem(''); setErr('');
  };

  const exportProblemCSV = (p, rows) => {
    const header = p.cols.map((c) => c.label);
    const lines = [header.map(csvEscape).join(',')];
    rows.forEach((r) => lines.push(p.cols.map((c) => csvEscape(c.get(r))).join(',')));
    downloadCSV(lines.join('\n'), `ESDA_${qid(p.id)}_${today()}.csv`);
  };

  // ================= RENDER =================
  const stepper = (
    <div className={classes.stepper}>
      {STEPS.map((label, i) => {
        const n = i + 1;
        const state = step === n ? 'active' : step > n ? 'done' : 'idle';
        const dotCls = `${classes.stepDot} ${state === 'active' ? classes.stepDotActive : state === 'done' ? classes.stepDotDone : classes.stepDotIdle}`;
        return (
          <div className={classes.stepItem} key={label}>
            <div className={dotCls}>{state === 'done' ? '✓' : n}</div>
            <span className={classes.stepLabel} style={{ color: state === 'idle' ? '#707070' : '#000' }}>{label}</span>
            {n < STEPS.length && <div className={`${classes.stepLine} ${step > n ? classes.stepLineActive : ''}`} />}
          </div>
        );
      })}
    </div>
  );

  // ---- Step 1 ----
  const renderStep1 = () => (
    <>
      <div className={`${classes.card} ${classes.cardPad}`} style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <Typography className={classes.h1}>What Would You Like To Check Today?</Typography>
          <div className={classes.muted} style={{ marginTop: '4px' }}>Select one or more data-quality issues to analyze. Required source files will be requested next.</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button className={classes.btnWhite} onClick={selectAll}>Select All 17</button>
          <button className={classes.btnWhite} onClick={clearAll}>Clear</button>
        </div>
      </div>
      <div className={classes.grid}>
        {PROBLEMS.map((p) => {
          const on = selected.has(p.id);
          return (
            <div key={p.id} className={`${classes.problemCard} ${on ? classes.problemCardSel : ''}`} onClick={() => toggleProblem(p.id)}>
              <div className={`${classes.checkbox} ${on ? classes.checkboxOn : ''}`}>{on ? '✓' : ''}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '11px', color: '#B4B4B4', fontWeight: 500 }}>#{String(p.id).padStart(2, '0')}</div>
                <div className={classes.pcTitle}>{p.title}</div>
                {p.files.length > 0 && (
                  <div className={classes.chipRow}>
                    {p.files.map((f) => <span key={f} className={`${classes.chip} ${classes.chipGrey}`}>{f}</span>)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className={classes.footerBar}>
        <div style={{ fontSize: '13px' }}><b>{selected.size}</b> <span className={classes.muted}>of 17 problems selected</span></div>
        <button className={`${classes.btnOrange} ${selected.size === 0 ? classes.btnOrangeOff : ''}`} disabled={selected.size === 0} onClick={() => setStep(2)}>Continue To Upload Data →</button>
      </div>
    </>
  );

  // ---- Step 2 ----
  const renderStep2 = () => (
    <>
      <div className={`${classes.card} ${classes.cardPad}`} style={{ marginBottom: '1.5rem' }}>
        <Typography className={classes.h1}>Upload Required Data Files</Typography>
        <div className={classes.muted} style={{ marginTop: '4px' }}>
          Based on your {selected.size} selected problem{selected.size > 1 ? 's' : ''}, we need <b>{requiredFiles.length} file{requiredFiles.length !== 1 ? 's' : ''}</b>. Click to upload each one.
        </div>
      </div>
      {requiredFiles.length === 0 ? (
        <div className={`${classes.card} ${classes.cardPad}`} style={{ textAlign: 'center', color: '#707070' }}>
          The selected problem(s) need no uploaded files. You can run the analysis directly.
        </div>
      ) : (
        <div className={classes.uploadGrid}>
          {requiredFiles.map((f) => {
            const up = uploaded[f];
            return (
              <div key={f} className={`${classes.uploadZone} ${up ? classes.uploadZoneOk : ''}`}>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{f}</div>
                <div className={classes.muted} style={{ marginTop: '2px' }}>{FILE_META[f]?.desc || ''}</div>
                {up ? (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ color: '#329429', fontWeight: 500, fontSize: '13px' }}>✓ {up.name}</div>
                    <div className={classes.muted}>{up.size}</div>
                    <button className={classes.btnLink} style={{ marginTop: '4px' }} onClick={() => pickFile(f)}>Replace</button>
                  </div>
                ) : (
                  <div style={{ marginTop: '12px' }}>
                    <button className={classes.btnWhite} onClick={() => pickFile(f)}>Choose File</button>
                    <div style={{ fontSize: '11px', color: '#B4B4B4', marginTop: '8px' }}>.xlsx · .csv · Max 50 MB</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {err && <div style={{ color: '#FF0004', fontSize: '13px', marginTop: '12px' }}>{err}</div>}
      <div className={classes.footerBar}>
        <div style={{ fontSize: '13px' }}><b>{requiredFiles.filter((f) => uploaded[f]).length}</b> <span className={classes.muted}>of {requiredFiles.length} files uploaded</span></div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className={classes.btnWhite} onClick={() => setStep(1)}>← Back</button>
          <button className={`${classes.btnOrange} ${!(allUploaded || requiredFiles.length === 0) ? classes.btnOrangeOff : ''}`} disabled={!(allUploaded || requiredFiles.length === 0)} onClick={runAnalysis}>Run Analysis →</button>
        </div>
      </div>
    </>
  );

  // ---- Step 3 ----
  const renderStep3 = () => {
    const steps = progress?.steps || [];
    const stepById = {};
    steps.forEach((s) => { stepById[s.id] = s; });
    const sel = [...selected].map((id) => PROBLEMS.find((p) => p.id === id));
    const done = steps.filter((s) => ['success', 'error', 'skipped'].includes(s.status)).length;
    const pct = sel.length ? Math.round((done / sel.length) * 100) : 0;
    const status = progress?.run_status;
    const paused = !running && !analysisDone;
    return (
      <>
        <div className={`${classes.card} ${classes.cardPad}`} style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <Typography className={classes.h1}>{analysisDone ? 'Analysis Complete' : 'Running Analysis…'}</Typography>
            <div className={classes.muted} style={{ marginTop: '4px' }}>Each selected problem is checked against your uploaded data.</div>
            <div className={classes.progressBar}>
              {!analysisDone && pct === 0
                ? <div className={classes.progressIndet} />
                : <div className={classes.progressFill} style={{ width: pct + '%' }} />}
            </div>
            <div className={classes.muted} style={{ marginTop: '6px' }}>{done} of {sel.length} checks done · {pct}%</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            {paused
              ? <button className={classes.btnGhost} onClick={resumeAnalysis} disabled={analysisDone}>▶ Resume</button>
              : <button className={classes.btnGhost} onClick={stopAnalysis} disabled={analysisDone}>❚❚ Stop</button>}
            <button className={`${classes.btnGhost} ${classes.btnGhostRed}`} onClick={resetAnalysis}>✕ Reset</button>
          </div>
        </div>
        {sel.map((p) => {
          const s = stepById[qid(p.id)];
          const st = s?.status || 'queued';
          const sev = SEVERITY[p.severity];
          let right = '⏳ Queued';
          if (st === 'success') right = `✓ ${s.issue_count} ${s.issue_count === 1 ? 'issue' : 'issues'}`;
          else if (st === 'error') right = `✕ ${s.error_reason || 'Failed'}`;
          else if (st === 'skipped') right = '— Skipped';
          else if (s) right = '◐ Analyzing…';
          return (
            <div key={p.id} className={classes.statusRow}>
              <div className={classes.sevBar} style={{ background: sev.color }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>#{String(p.id).padStart(2, '0')} {p.title}</div>
              </div>
              <div style={{ fontSize: '13px', color: st === 'error' ? '#FF0004' : st === 'success' ? '#329429' : '#707070', whiteSpace: 'nowrap' }}>{right}</div>
            </div>
          );
        })}
        {err && <div style={{ color: '#FF0004', fontSize: '13px', marginTop: '12px' }}>{err}</div>}
        <div className={classes.footerBar}>
          <div style={{ fontSize: '13px' }}><b>{steps.filter((s) => s.status === 'success').length}</b> <span className={classes.muted}>of {sel.length} checks succeeded</span></div>
          <button className={`${classes.btnOrange} ${!analysisDone ? classes.btnOrangeOff : ''}`} disabled={!analysisDone} onClick={goToResults}>View Results →</button>
        </div>
      </>
    );
  };

  // ---- Step 4 ----
  const renderResultCard = (p) => {
    const all = resultsByProblem[qid(p.id)] || [];
    const rows = all.filter(rowMatchesFilters);
    const sev = SEVERITY[p.severity];
    return (
      <details className={classes.resultCard} key={p.id} open={all.length > 0}>
        <summary className={classes.resultHead}>
          <div className={classes.sevBar} style={{ background: sev.color, minHeight: '40px' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '11px', color: '#B4B4B4', fontWeight: 500 }}>#{String(p.id).padStart(2, '0')}</div>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>{p.title}</div>
            <div className={classes.muted} style={{ marginTop: '2px' }}>{CATEGORY_LABELS[p.category]} · Owner: {p.owner}</div>
          </div>
          <div>
            <div className={classes.countNum}>{all.length.toLocaleString()}</div>
            <div style={{ fontSize: '11px', color: '#707070', textTransform: 'uppercase', textAlign: 'right' }}>{all.length === 1 ? 'Issue' : 'Issues'}</div>
          </div>
        </summary>
        <div style={{ borderTop: '1px solid #E4E2E3' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className={classes.table}>
              <thead><tr>{p.cols.map((c) => <th key={c.key} className={classes.th}>{c.label}</th>)}</tr></thead>
              <tbody>
                {rows.length > 0 ? rows.map((r, i) => (
                  <tr key={i}>{p.cols.map((c) => <td key={c.key} className={classes.td}>{c.get(r)}</td>)}</tr>
                )) : (
                  <tr>
                    <td className={classes.td} colSpan={p.cols.length} style={{ textAlign: 'center', color: '#707070', padding: '16px' }}>
                      {all.length > 0 ? 'No rows match the current filters.' : 'No issues found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#F9F9FC' }}>
              <span className={classes.muted}>{rows.length} {rows.length === 1 ? 'row' : 'rows'}</span>
              <button className={classes.btnLink} onClick={() => exportProblemCSV(p, rows)} disabled={rows.length === 0} style={rows.length === 0 ? { opacity: 0.4, cursor: 'not-allowed' } : {}}>Export Results (CSV)</button>
            </div>
          </div>
          <div className={classes.actionBox}>
            <div style={{ fontSize: '11px', fontWeight: 500, color: '#286ACF', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '4px' }}>How To Fix</div>
            <div style={{ fontSize: '13px' }}>{p.action}</div>
          </div>
        </div>
      </details>
    );
  };

  const renderStep4 = () => {
    const shown = [...selected].map((id) => PROBLEMS.find((p) => p.id === id))
      .filter((p) => !fProblem || p.id === Number(fProblem));
    return (
      <>
        <div className={`${classes.card} ${classes.cardPad}`}>
          <div className={classes.filterBar}>
            <div className={classes.searchField}>
              <div className={classes.searchPill}>
                <input className={classes.searchInput} placeholder="Search using PO Number, Batch Number, Product Code" value={search} onChange={(e) => setSearch(e.target.value)} />
                <button className={classes.searchBtn} onClick={() => fetchResults()}>Search</button>
              </div>
            </div>
            <div className={classes.filterField}>
              <label className={classes.filterLabel}>CMO:</label>
              <select className={classes.select} value={fCmo} onChange={(e) => setFCmo(e.target.value)}>
                <option value="">All</option>
                {cmoOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className={classes.filterField}>
              <label className={classes.filterLabel}>Brand:</label>
              <select className={classes.select} value={fBrand} onChange={(e) => setFBrand(e.target.value)}>
                <option value="">All</option>
                {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className={classes.filterField}>
              <label className={classes.filterLabel}>Problem:</label>
              <select className={classes.select} value={fProblem} onChange={(e) => setFProblem(e.target.value)}>
                <option value="">All</option>
                {[...selected].map((id) => PROBLEMS.find((p) => p.id === id)).map((p) => <option key={p.id} value={p.id}>#{String(p.id).padStart(2, '0')} {p.title}</option>)}
              </select>
            </div>
            <button className={classes.btnBlue} onClick={restart}>Start New Analysis</button>
          </div>
        </div>
        {loadingResults ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}><CircularProgress size={28} style={{ color: '#EC6535' }} /></div>
        ) : shown.map(renderResultCard)}
      </>
    );
  };

  return (
    <div className={classes.root}>
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onFileChosen} />
      {stepper}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  );
};

const mapStateToProps = () => ({});
export default connect(mapStateToProps, {})(withStyles(styles)(withRouter(Esda)));
